import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form, Input, InputNumber, Modal, Select, Space, Table, message } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import PageHeader from '../../components/business/PageHeader';
import PermissionGuard from '../../components/business/PermissionGuard';
import RequestState from '../../components/business/RequestState';
import StatusTag from '../../components/business/StatusTag';
import { PageResult, WorkTask } from '../../models/v2';
import { V2ApiError, v2Api } from '../../services/v2';
import { asV2Error, formatDateTime } from '../../utils/v2';

const emptyPage: PageResult<WorkTask> = { items: [], pageNo: 1, pageSize: 20, total: 0 };

const TasksPage = () => {
    const [form] = Form.useForm();
    const [transitionForm] = Form.useForm();
    const [data, setData] = useState(emptyPage);
    const [filters, setFilters] = useState<any>({ pageNo: 1, pageSize: 20 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<V2ApiError | null>(null);
    const [createVisible, setCreateVisible] = useState(false);
    const [actionDialog, setActionDialog] = useState<{ row: WorkTask; action: string } | null>(
        null
    );
    const [saving, setSaving] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await v2Api.tasks(filters));
        } catch (e) {
            setError(asV2Error(e));
        } finally {
            setLoading(false);
        }
    }, [filters]);
    useEffect(() => {
        load();
    }, [load]);

    const transition = async (row: WorkTask, action: string, values: any = {}) => {
        try {
            await v2Api.transitionTask(row.id, action, {
                ...values,
                version: row.version,
                reason: values.reason || `从任务列表执行${action}`,
                nextActionAt: values.nextActionAt
                    ? new Date(values.nextActionAt).toISOString()
                    : undefined,
            });
            message.success('任务状态已更新');
            setActionDialog(null);
            transitionForm.resetFields();
            load();
        } catch (e) {
            const err = asV2Error(e);
            message.error(
                err.code === 'VERSION_CONFLICT' ? '任务已被他人更新，请刷新后重试' : err.message
            );
        }
    };
    const claim = async (row: WorkTask) => {
        try {
            await v2Api.assignTask(row.id, 'claim', {
                version: row.version,
                reason: '从任务列表认领',
            });
            message.success('任务已认领');
            load();
        } catch (e) {
            message.error(asV2Error(e).message);
        }
    };
    const submitTransition = async () => {
        if (!actionDialog) return;
        const values = await transitionForm.validateFields();
        await transition(actionDialog.row, actionDialog.action, values);
    };
    const create = async () => {
        const values = await form.validateFields();
        const body = {
            ...values,
            sourceId: Number(values.sourceId),
            companyId: Number(values.companyId),
            assigneeId: values.assigneeId ? Number(values.assigneeId) : undefined,
            dueAt: values.dueAt ? new Date(values.dueAt).toISOString() : undefined,
        };
        setSaving(true);
        try {
            await v2Api.createTask(body);
            message.success('任务已创建');
            form.resetFields();
            setCreateVisible(false);
            load();
        } catch (e) {
            message.error(asV2Error(e).message);
        } finally {
            setSaving(false);
        }
    };
    const columns = [
        {
            title: '任务',
            dataIndex: 'title',
            render: (v: string, row: WorkTask) => (
                <div>
                    <strong>{v}</strong>
                    <div className="v2-muted">
                        {row.taskType || '人工任务'} ·{' '}
                        {row.standardName || row.shortName || `企业 #${row.companyId}`}
                    </div>
                </div>
            ),
        },
        {
            title: '优先级',
            dataIndex: 'priorityCode',
            width: 100,
            render: (v: string) => <StatusTag value={v} />,
        },
        {
            title: '负责人',
            dataIndex: 'assigneeName',
            width: 110,
            render: (v: string, row: WorkTask) => v || row.assigneeId || '待分配',
        },
        { title: '截止时间', dataIndex: 'dueAt', width: 170, render: formatDateTime },
        { title: '下一步', dataIndex: 'nextAction', width: 190, render: (v: string) => v || '—' },
        {
            title: '状态',
            dataIndex: 'status',
            width: 110,
            render: (v: string) => <StatusTag value={v} />,
        },
        {
            title: '操作',
            key: 'action',
            width: 190,
            render: (_: any, row: WorkTask) => (
                <Space>
                    {row.status === 'UNASSIGNED' && (
                        <Button type="link" onClick={() => claim(row)}>
                            认领
                        </Button>
                    )}
                    {['READY', 'WAITING'].includes(row.status) && (
                        <Button type="link" onClick={() => transition(row, 'start')}>
                            {row.status === 'WAITING' ? '继续' : '开始'}
                        </Button>
                    )}
                    {row.status === 'IN_PROGRESS' && (
                        <Button
                            type="link"
                            onClick={() => setActionDialog({ row, action: 'wait' })}
                        >
                            等待
                        </Button>
                    )}
                    {['IN_PROGRESS', 'WAITING'].includes(row.status) && (
                        <Button
                            type="link"
                            onClick={() => setActionDialog({ row, action: 'complete' })}
                        >
                            完成
                        </Button>
                    )}
                </Space>
            ),
        },
    ];
    return (
        <div className="v2-page">
            <PageHeader
                eyebrow="工作台 · 来源对象"
                title="我的任务"
                description="工作台与任务列表共享同一任务事实；领取、分配、转派和状态变化均由服务端校验版本。"
                extra={
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={load}>
                            刷新
                        </Button>
                        <PermissionGuard anyOf={['TASK_ASSIGN']}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setCreateVisible(true)}
                            >
                                新建任务
                            </Button>
                        </PermissionGuard>
                    </Space>
                }
            />
            <Card className="v2-filter-card">
                <Space className="v2-wrap-space">
                    <Select
                        allowClear
                        placeholder="任务状态"
                        style={{ width: 150 }}
                        onChange={(status) => setFilters({ ...filters, status, pageNo: 1 })}
                    >
                        {[
                            'UNASSIGNED',
                            'READY',
                            'IN_PROGRESS',
                            'WAITING',
                            'CLOSED',
                            'CANCELLED',
                        ].map((item) => (
                            <Select.Option key={item} value={item}>
                                {item}
                            </Select.Option>
                        ))}
                    </Select>
                    <InputNumber
                        min={1}
                        placeholder="企业 ID"
                        onChange={(companyId) => setFilters({ ...filters, companyId, pageNo: 1 })}
                    />
                    <InputNumber
                        min={1}
                        placeholder="负责人 ID"
                        onChange={(assigneeId) => setFilters({ ...filters, assigneeId, pageNo: 1 })}
                    />
                </Space>
            </Card>
            <Card className="v2-panel" bordered={false}>
                <RequestState
                    loading={loading}
                    error={error}
                    empty={!data.items.length}
                    onRetry={load}
                >
                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={data.items}
                        scroll={{ x: 1000 }}
                        pagination={{
                            current: data.pageNo,
                            pageSize: data.pageSize,
                            total: data.total,
                            showSizeChanger: true,
                            onChange: (pageNo, pageSize) =>
                                setFilters({ ...filters, pageNo, pageSize }),
                        }}
                    />
                </RequestState>
            </Card>
            <Modal
                title="创建人工任务"
                visible={createVisible}
                onOk={create}
                confirmLoading={saving}
                onCancel={() => setCreateVisible(false)}
                okText="创建"
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        taskType: 'FOLLOW_UP',
                        sourceType: 'COMPANY',
                        priorityCode: 'NORMAL',
                    }}
                >
                    <Form.Item label="任务标题" name="title" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item label="目标" name="objective">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Space style={{ display: 'flex' }}>
                        <Form.Item label="企业 ID" name="companyId" rules={[{ required: true }]}>
                            <InputNumber min={1} />
                        </Form.Item>
                        <Form.Item label="负责人 ID" name="assigneeId">
                            <InputNumber min={1} />
                        </Form.Item>
                    </Space>
                    <Space style={{ display: 'flex' }}>
                        <Form.Item label="来源类型" name="sourceType" rules={[{ required: true }]}>
                            <Select style={{ width: 160 }}>
                                <Select.Option value="COMPANY">企业</Select.Option>
                                <Select.Option value="SIGNAL">客户信号</Select.Option>
                                <Select.Option value="OPPORTUNITY">商机</Select.Option>
                                <Select.Option value="PROJECT">项目</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item label="来源对象 ID" name="sourceId" rules={[{ required: true }]}>
                            <InputNumber min={1} />
                        </Form.Item>
                    </Space>
                    <Form.Item label="任务类型" name="taskType" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item label="截止时间" name="dueAt">
                        <Input type="datetime-local" />
                    </Form.Item>
                    <Form.Item label="下一步行动" name="nextAction">
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                title={
                    actionDialog && (actionDialog.action === 'wait' ? '将任务设为等待' : '完成任务')
                }
                visible={!!actionDialog}
                onOk={submitTransition}
                onCancel={() => setActionDialog(null)}
                okText="确认"
            >
                <Form form={transitionForm} layout="vertical">
                    <Form.Item label="操作原因" name="reason" rules={[{ required: true }]}>
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    {actionDialog && actionDialog.action === 'wait' && (
                        <>
                            <Form.Item
                                label="等待对象 / 事项"
                                name="waitingFor"
                                rules={[{ required: true }]}
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                label="下一步处理时间"
                                name="nextActionAt"
                                rules={[{ required: true }]}
                            >
                                <Input type="datetime-local" />
                            </Form.Item>
                        </>
                    )}
                    {actionDialog && actionDialog.action === 'complete' && (
                        <Form.Item label="完成结果" name="resultCode" rules={[{ required: true }]}>
                            <Select>
                                <Select.Option value="DONE">已完成</Select.Option>
                                <Select.Option value="NO_FURTHER_ACTION">无需继续</Select.Option>
                                <Select.Option value="FOLLOW_UP_CREATED">已安排后续</Select.Option>
                            </Select>
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </div>
    );
};
export default TasksPage;
